'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  examPaceMinutes,
  formatMarks,
  type PracticeAttemptKind,
  type PracticeLevel,
  type PracticeSessionQuestion
} from '@repo/api-client/content-management-service';

import {
  levelAllowsAssists
} from '@/app/lib/practice/levels';

import {
  feedbackFor,
  type AttemptOutcome
} from '@/app/lib/practice/session-view';

import type {
  PracticeQuestion,
  PracticeReason,
  PracticeReasonStepView,
  PracticeSolutionView
} from '@/app/lib/practice/types';

import type {
  ReasonsView
} from '@/app/lib/practice/use-practice-session';

import {
  ReasonsPanel,
  reasonsResultView
} from './practice-reasons';

import {
  PracticeAnswerBox,
  type AnswerBoxHandle
} from './practice-answer-box';

import {
  PracticeFigure
} from './practice-figure';

// One practice question, solved (spec: Brain elite-scholars/30-projects/
// practice-solving-ux-spec.md). Laid out by the width of the practice area,
// not the device (container queries on @container/solve, set by the page):
//
//   under 600px   one column: pills, question, figure, answer card;
//   600 to 899px  one column, larger type, options in two columns;
//   900px and up  two panes: the question (sticky) left, the answer right.
//
// Every verdict and every mark comes from the server's session; this view
// only says what the learner did.
//
// The page holds no answer and no help: hints, the options, the "why" of a
// wrong pick and the worked solution are fetched from the server when the
// learner asks (PracticeHelpApi), and the server records each before it
// sends it.

export type AttemptExtra = {
  answer?: string
  format?: 'latex' | 'text'
  prompts?: Record<string, string>
  choiceIndex?: number
  /** kind 'reasons': the reason id picked per step, by step number. */
  reasons?: Record<string, string>
};

export type PostAttempt = (questionId: string, kind: Extract<PracticeAttemptKind, 'typed' | 'choice' | 'skipped' | 'reasons'>, extra?: AttemptExtra) => Promise<AttemptOutcome | null>;

/** Help, fetched on demand. Each returns null when the server refused or could not be reached. */
export interface PracticeHelpApi {
  hint: (questionId: string, index: number) => Promise<string | null>
  options: (questionId: string) => Promise<string[] | null>
  solution: (questionId: string) => Promise<PracticeSolutionView | null>
  /** Geometry: the reason steps, once the value is right. */
  reasons?: (questionId: string) => Promise<ReasonsView | null>
}

const DIFFICULTY: Record<1 | 2 | 3, string> = {
  1: 'Warm-up',
  2: 'Exam standard',
  3: 'Stretch'
};

const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(1,158,250)]';

const SOFT = `min-h-[44px] px-4 rounded-full text-sm font-semibold border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:border-[rgba(1,158,250,0.5)] transition-colors motion-reduce:transition-none disabled:opacity-50 disabled:cursor-default ${ FOCUS }`;

const MAIN = `min-h-[44px] px-5 rounded-full text-sm font-semibold bg-[rgb(1,158,250)] text-white hover:bg-[rgb(0,140,225)] transition-colors motion-reduce:transition-none disabled:opacity-60 ${ FOCUS }`;

const PILL = 'items-center text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';

/** Server-rendered HTML with MathML, from our own question bank; nothing a
 * user typed is ever passed through here. */
function MathHtml({
  html, className = '', as = 'div'
}: { html: string
  className?: string
  as?: 'div' | 'span' }) {

  const Tag = as;

  return (
    <Tag
      className={`practice-maths ${ className }`}
      dangerouslySetInnerHTML={{
        __html: html
      }}
    />
  );
}

interface FeedbackLine {
  tone: 'good' | 'bad' | 'muted'
  text: string
}

export function PracticeQuestionView({
  question, index, total, topicLabel, level = 'guided', state, onAttempt, help, onPaste, onSkipped, reasonList = null
}: {
  question: PracticeQuestion
  index: number
  total: number
  /** "Paper 1 · Algebra" */
  topicLabel: string
  /** The session's level, fixed for the session. Exam mode: no hints or options, a Skip button. */
  level?: PracticeLevel
  /** This question in the server's session. */
  state: PracticeSessionQuestion | undefined
  onAttempt: PostAttempt
  help: PracticeHelpApi
  onPaste: () => void
  /** After a skip is saved: the page moves on to the next unanswered question. */
  onSkipped?: () => void
  /** The pack's full reason list (geometry), for "Another reason". */
  reasonList?: PracticeReason[] | null
}) {

  const [ feedback, setFeedback ] = useState<FeedbackLine | null>(null);
  const [ checking, setChecking ] = useState(false);
  // Hints the server has sent, in order.
  const [ hints, setHints ] = useState<string[]>([]);
  const [ loadingHelp, setLoadingHelp ] = useState<null | 'hint' | 'options' | 'solution'>(null);
  const [ solution, setSolution ] = useState<PracticeSolutionView | null>(null);
  const [ solutionOpen, setSolutionOpen ] = useState(false);
  const [ wrongOnce, setWrongOnce ] = useState(false);
  const [ promptStates, setPromptStates ] = useState<Record<string, boolean> | null>(null);
  // Typed answers first; the four options are a fallback (Governor,
  // 8 October 2026) and cost half the marks once shown. Their HTML comes
  // from the server, with no flag for the right one.
  const [ options, setOptions ] = useState<string[] | null>(null);
  // Each pick's verdict, from the server.
  const [ picks, setPicks ] = useState<Record<number, boolean | null>>({});
  // Why a wrong pick is tempting, from the server, per picked option.
  const [ whys, setWhys ] = useState<Record<number, string | null>>({});
  const [ lastPick, setLastPick ] = useState<number | null>(null);
  // Geometry with reasons: the steps arrive once the value is right.
  const [ reasonSteps, setReasonSteps ] = useState<PracticeReasonStepView[] | null>(null);
  const [ reasonsMemo, setReasonsMemo ] = useState(false);
  const [ reasonsResult, setReasonsResult ] = useState<{ detail: boolean[]
    picked?: string[]
    earned?: number
    total?: number } | null>(null);
  const [ reasonsBusy, setReasonsBusy ] = useState(false);
  // Words from the answer engine that do not change the mark.
  const [ note, setNote ] = useState<string | null>(null);
  const boxRef = useRef<AnswerBoxHandle | null>(null);
  const assists = levelAllowsAssists(level);
  const isExam = level === 'exam';
  const hasOptions = question.hasOptions && assists;
  const optionsOpen = options !== null;
  const isCorrect = state?.status === 'correct';

  // The state BEFORE an attempt, for wording the server's verdict.
  const stateRef = useRef(state);

  useEffect(() => {

    stateRef.current = state;
  }, [ state ]);

  const verdict = useCallback(async (kind: 'typed' | 'choice', extra: AttemptExtra, offered: boolean): Promise<AttemptOutcome | null> => {

    const before = stateRef.current;

    setChecking(true);
    setFeedback({
      tone: 'muted',
      text: 'Checking…'
    });

    const outcome = await onAttempt(question.id, kind, extra);

    setChecking(false);

    if (!outcome) {

      setFeedback({
        tone: 'muted',
        text: 'That answer was not saved. Check your connection and try again.'
      });
      return null;
    }

    if (kind === 'typed') {

      setPromptStates(outcome.prompts ?? null);

      if (outcome.correct === false) {

        setWrongOnce(true);
      }
    }

    // A correct answer brings the worked solution with it, at no cost, for
    // "Compare your method".
    if (outcome.solution) {

      setSolution(outcome.solution);
    }

    setNote(outcome.message ?? null);

    // Geometry: a right value brings the reason steps (their statements give
    // the value away, so they only come now).
    if (outcome.reasons) {

      setReasonSteps(outcome.reasons);
      setReasonsMemo(false);
      setFeedback({
        tone: 'good',
        text: `Right value: ${ formatMarks(outcome.marksAwarded) } ${ outcome.marksAwarded === 1 ? 'mark' : 'marks' }. Now give a reason for each step below.`
      });

      return outcome;
    }

    setFeedback(feedbackFor(outcome, question.marks, before, offered, assists));

    return outcome;
  }, [ onAttempt, question.id, question.marks, assists ]);

  // Back on a geometry question whose value is already right (a reload, or
  // Continue later): fetch its reason steps, with the memo once marked.
  const loadReasons = useRef(question.reasonSteps > 0
    && (state?.status === 'correct' || state?.status === 'revealed')
    && Boolean(help.reasons));

  useEffect(() => {

    if (!loadReasons.current || !help.reasons) {

      return;
    }

    loadReasons.current = false;
    void help.reasons(question.id).then((view) => {

      if (!view) {

        return;
      }

      setReasonSteps(view.steps);
      setReasonsMemo(view.memo);

      if (view.detail) {

        setReasonsResult({
          detail: view.detail
        });
      }
    });
  }, [ help, question.id ]);

  const submitReasons = async (picks: Record<string, string>) => {

    if (reasonsBusy) {

      return;
    }

    setReasonsBusy(true);
    const outcome = await onAttempt(question.id, 'reasons', {
      reasons: picks
    });
    setReasonsBusy(false);

    if (!outcome?.reasonsResult) {

      setFeedback({
        tone: 'muted',
        text: 'Your reasons were not saved. Check your connection and try again.'
      });
      return;
    }

    const view = reasonsResultView(outcome.reasonsResult);

    setReasonSteps(view.steps);
    setReasonsMemo(true);
    setReasonsResult(view);
    setFeedback({
      tone: 'good',
      text: `Reasons marked: ${ view.earned } of ${ view.total }. Compare your method with the worked solution.`
    });
  };

  const skip = async () => {

    if (checking) {

      return;
    }

    setChecking(true);
    const outcome = await onAttempt(question.id, 'skipped');
    setChecking(false);

    setFeedback(outcome
      ? {
        tone: 'muted',
        text: 'Skipped. It earns no marks for now; you can come back to it before you finish.'
      }
      : {
        tone: 'muted',
        text: 'That was not saved. Check your connection and try again.'
      });

    if (outcome) {

      onSkipped?.();
    }
  };

  const check = useCallback(() => {

    if (checking) {

      return;
    }

    const payload = boxRef.current?.read() ?? null;

    // Empty input is caught here so nothing is sent; the verdict itself is
    // always the server's.
    if (!payload) {

      setFeedback({
        tone: 'muted',
        text: 'Type your answer first.'
      });
      return;
    }

    boxRef.current?.hideKeyboard();
    void verdict('typed', payload, hasOptions && !optionsOpen);
  }, [ checking, verdict, hasOptions, optionsOpen ]);

  const loadOptions = useCallback(async () => {

    setLoadingHelp('options');
    const html = await help.options(question.id);
    setLoadingHelp(null);

    if (html) {

      setOptions(html);
    } else {

      setFeedback({
        tone: 'muted',
        text: 'The options did not load. Please try again.'
      });
    }
  }, [ help, question.id ]);

  // Options already opened in this session (before a reload) open again.
  const reopenOptions = useRef(state?.assist === 'options' && state.status !== 'correct' && hasOptions);

  useEffect(() => {

    if (reopenOptions.current) {

      reopenOptions.current = false;
      void loadOptions();
    }
  }, [ loadOptions ]);

  const nextHint = async () => {

    if (loadingHelp || hints.length >= question.hintsCount) {

      return;
    }

    setLoadingHelp('hint');
    const html = await help.hint(question.id, hints.length);
    setLoadingHelp(null);

    if (html) {

      setHints((h) => [ ...h, html ]);
    } else {

      setFeedback({
        tone: 'muted',
        text: 'That hint did not load. Please try again.'
      });
    }
  };

  const pick = (i: number) => {

    if (!options?.[i] || i in picks || checking) {

      return;
    }

    // The pick is coloured by the server's verdict, not by anything the page
    // holds about which option is right.
    setPicks((p) => ({
      ...p,
      [i]: null
    }));
    setLastPick(i);
    void verdict('choice', {
      choiceIndex: i
    }, false).then((outcome) => {

      setPicks((p) => ({
        ...p,
        [i]: outcome ? outcome.correct === true : null
      }));

      if (outcome?.whyHtml) {

        setWhys((w) => ({
          ...w,
          [i]: outcome.whyHtml ?? null
        }));
      }
    });
  };

  const toggleSolution = async () => {

    if (solutionOpen) {

      setSolutionOpen(false);
      return;
    }

    // Geometry: the solution gives the reasons away, so opening it before
    // they are marked forfeits the reason marks (the server records it).
    if (reasonSteps && !reasonsMemo && !reasonsResult
      && !window.confirm('The worked solution shows the reasons. If you open it now, your reasons for this question earn no marks. Open it anyway?')) {

      return;
    }

    if (!solution) {

      if (loadingHelp) {

        return;
      }

      setLoadingHelp('solution');
      const view = await help.solution(question.id);
      setLoadingHelp(null);

      if (!view) {

        setFeedback({
          tone: 'muted',
          text: 'The worked solution did not load. Please try again.'
        });
        return;
      }

      setSolution(view);

      // The memo's reasons now show in the panel too.
      if (view.reasons && reasonSteps) {

        setReasonSteps(view.reasons);
        setReasonsMemo(true);
      }
    }

    setSolutionOpen(true);
  };

  const pace = examPaceMinutes(question.marks);

  return (
    <article
      aria-labelledby={`q-${ question.id }`}
      className="grid grid-cols-[minmax(0,1fr)] gap-5 @min-[900px]/solve:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] @min-[900px]/solve:gap-8 @min-[900px]/solve:items-start"
    >
      {/* The question: sticky beside the answer on wide screens. */}
      <section className="min-w-0 space-y-3 @min-[900px]/solve:sticky @min-[900px]/solve:top-28">
        <div className="flex flex-wrap items-center gap-2">
          <span id={`q-${ question.id }`} className={`${ PILL } inline-flex`}>{`Question ${ index + 1 } of ${ total }`}</span>
          <span className={`${ PILL } hidden @min-[600px]/solve:inline-flex`}>{topicLabel}</span>
          <span className={`${ PILL } inline-flex`}>{`${ question.marks } ${ question.marks === 1 ? 'mark' : 'marks' } · ${ DIFFICULTY[question.difficulty] }`}</span>
          {isExam ? (
            <span className="inline-flex items-center text-sm font-semibold px-3.5 py-1.5 rounded-full bg-[rgb(1,158,250)] text-white">
              {`Exam pace: about ${ pace } min`}
            </span>
          ) : (
            <span className={`${ PILL } inline-flex`}>{`Exam pace: about ${ pace } min`}</span>
          )}
          {question.calculator && (
            <span className={`${ PILL } inline-flex`}>Calculator</span>
          )}
          {isCorrect && state && (
            <span className={`${ PILL } inline-flex bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300`}>
              {state.marksAwarded >= question.marks ? 'Correct' : `Correct · ${ formatMarks(state.marksAwarded) } of ${ question.marks }`}
            </span>
          )}
        </div>

        <div className="space-y-2 text-[#111111] dark:text-white">
          <MathHtml html={question.promptHtml} className="text-[19px] leading-7 @min-[600px]/solve:text-[21px] @min-[600px]/solve:leading-[30px]" />
          {question.displayHtml && (
            <MathHtml
              html={question.displayHtml}
              className="rounded-xl bg-gray-50 dark:bg-gray-800/60 px-4 py-3 overflow-x-auto overflow-y-hidden whitespace-nowrap text-[22px]"
            />
          )}
          {question.afterHtml && (
            <MathHtml html={question.afterHtml} className="text-[19px] leading-7 @min-[600px]/solve:text-[21px] @min-[600px]/solve:leading-[30px]" />
          )}
        </div>

        {question.figure && <PracticeFigure figure={question.figure} />}
      </section>

      {/* The answer card. Paste events cross MathLive's shadow root (they
          are composed), so one capture listener sees both kinds of box. */}
      <section className="min-w-0 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 @min-[600px]/solve:p-5 space-y-3" onPasteCapture={onPaste}>
        <p className="text-sm text-gray-500 dark:text-gray-400">Your answer</p>

        <PracticeAnswerBox
          ref={boxRef}
          questionId={question.id}
          input={question.input}
          ariaLabel={`Your answer to question ${ index + 1 }`}
          onEnter={check}
          promptStates={promptStates}
        />

        <div className="flex flex-wrap gap-2.5">
          <button type="button" onClick={check} disabled={checking} className={MAIN}>
            {checking ? 'Checking…' : 'Check'}
          </button>
          {assists && question.hintsCount > 0 && (
            <button
              type="button"
              disabled={hints.length >= question.hintsCount || loadingHelp === 'hint'}
              onClick={() => void nextHint()}
              className={SOFT}
            >
              {hints.length >= question.hintsCount ? 'No more hints' : (hints.length === 0 ? 'Hint' : 'Next hint')}
            </button>
          )}
          {hasOptions && !optionsOpen && !isCorrect && (
            <button type="button" disabled={loadingHelp === 'options'} onClick={() => void loadOptions()} className={wrongOnce ? MAIN : SOFT}>
              Show options (half marks)
            </button>
          )}
          {isExam && !isCorrect && state?.status !== 'skipped' && (
            <button type="button" disabled={checking} onClick={() => void skip()} className={SOFT}>
              Skip
            </button>
          )}
          <button type="button" disabled={loadingHelp === 'solution'} onClick={() => void toggleSolution()} aria-expanded={solutionOpen} className={SOFT}>
            {solutionOpen ? 'Hide the worked solution' : 'Worked solution'}
          </button>
        </div>

        <p role="status" aria-live="polite" className="text-[15px] font-semibold min-h-[1.5rem]">
          {feedback && (
            <span className={feedback.tone === 'good'
              ? 'text-emerald-700 dark:text-emerald-400'
              : feedback.tone === 'bad' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 font-normal'}
            >
              {feedback.text}
            </span>
          )}
        </p>

        {note && (
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{note}</p>
        )}

        {reasonSteps && (
          <ReasonsPanel
            questionNumber={index + 1}
            steps={reasonSteps}
            reasonList={reasonList}
            result={reasonsResult}
            memo={reasonsMemo}
            busy={reasonsBusy}
            onSubmit={(picks) => void submitReasons(picks)}
          />
        )}

        {hints.length > 0 && <HintsPanel hintsHtml={hints} />}

        {options && (
          <OptionsPanel
            label={`Options for question ${ index + 1 }`}
            optionsHtml={options}
            picks={picks}
            whyHtml={lastPick !== null && picks[lastPick] === false ? whys[lastPick] ?? null : null}
            disabled={isCorrect || checking}
            onPick={pick}
          />
        )}

        {solutionOpen && solution && <SolutionPanel solution={solution} />}
      </section>
    </article>
  );
}

// ── Help panels ──────────────────────────────────────────────────────────────
// Hints, options and the worked solution each take only the data they show,
// which the server sent when the learner asked for it.

function HintsPanel({
  hintsHtml
}: { hintsHtml: string[] }) {

  return (
    <div className="rounded-xl bg-sky-50 dark:bg-sky-950/30 p-4 space-y-2">
      {hintsHtml.map((hint, i) => (
        <div key={i} className="text-[15px] text-[#111111] dark:text-gray-200 leading-relaxed">
          <span className="font-semibold">{`Hint ${ i + 1 }: `}</span>
          <MathHtml html={hint} as="span" />
        </div>
      ))}
    </div>
  );
}

function OptionsPanel({
  label, optionsHtml, picks, whyHtml, disabled, onPick
}: {
  label: string
  /** The four options, in display order, as the server sent them. */
  optionsHtml: string[]
  /** Index -> the server's verdict (null while it is being asked). */
  picks: Record<number, boolean | null>
  /** Why the last (wrong) pick is tempting, from the server. */
  whyHtml: string | null
  disabled: boolean
  onPick: (i: number) => void
}) {

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 dark:text-gray-400">Pick the right answer. A correct pick earns half marks.</p>
      <div role="group" aria-label={label} className="grid grid-cols-1 @min-[600px]/solve:grid-cols-2 gap-2.5">
        {optionsHtml.map((html, i) => {

          const result = picks[i];
          const wasPicked = i in picks;
          const tone = !wasPicked
            ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-[rgba(1,158,250,0.5)]'
            : result === true
              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
              : result === false
                ? 'border-red-400 bg-red-50 dark:bg-red-950/30'
                : 'border-[rgba(1,158,250,0.6)] bg-sky-50 dark:bg-sky-950/30';

          return (
            <button
              key={i}
              type="button"
              disabled={wasPicked || disabled}
              aria-pressed={wasPicked}
              onClick={() => onPick(i)}
              className={`min-h-[52px] text-left rounded-2xl border px-3.5 py-3 overflow-x-auto overflow-y-hidden whitespace-nowrap text-lg text-[#111111] dark:text-gray-100 transition-colors motion-reduce:transition-none disabled:cursor-default ${ FOCUS } ${ tone }`}
            >
              <MathHtml html={html} as="span" />
            </button>
          );
        })}
      </div>
      {whyHtml && (
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          <span className="font-semibold">Why that one is tempting: </span>
          <MathHtml html={whyHtml} as="span" />
        </p>
      )}
    </div>
  );
}

function SolutionPanel({
  solution
}: { solution: PracticeSolutionView }) {

  return (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-4 space-y-3">
      <p className="text-sm font-semibold text-[#111111] dark:text-white">Worked solution</p>
      {solution.keyStepsOnly && (
        <p className="text-xs text-gray-500 dark:text-gray-400">Exam mode shows the key steps, as a memo would.</p>
      )}
      <ol className="space-y-4 text-[#111111] dark:text-gray-100">
        {solution.steps.map((step) => (
          <li key={step.number} className="space-y-1">
            <span className="inline-block text-xs font-semibold text-[rgb(0,113,190)] dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 rounded-full px-2.5 py-0.5">
              {`Step ${ step.number }`}
            </span>
            {step.sayHtml && (
              <MathHtml html={step.sayHtml} className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300" />
            )}
            {/* A long line scrolls on its own on a phone instead of running off the card. */}
            {step.mathHtml && (
              <MathHtml html={step.mathHtml} className="overflow-x-auto overflow-y-hidden whitespace-nowrap py-1 text-lg" />
            )}
          </li>
        ))}
      </ol>
      {solution.reasons && solution.reasons.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
          <p className="text-sm font-semibold text-[#111111] dark:text-white">Statements and reasons</p>
          <ol className="space-y-3">
            {solution.reasons.map((r) => (
              <li key={r.step} className="space-y-1">
                <MathHtml html={r.statementHtml} className="overflow-x-auto overflow-y-hidden whitespace-nowrap py-1 text-lg text-[#111111] dark:text-gray-100" />
                <p className="text-[15px] text-gray-700 dark:text-gray-300">{`Reason: ${ r.memoReasonText ?? '' }`}</p>
              </li>
            ))}
          </ol>
        </div>
      )}
      {solution.noteHtml && (
        <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed border-t border-gray-200 dark:border-gray-700 pt-3">
          <span className="font-semibold">Exam tip: </span>
          <MathHtml html={solution.noteHtml} as="span" />
        </div>
      )}
    </div>
  );
}

/**
 * Score, Previous and Next, fixed to the bottom of the screen and lifted
 * above the maths keyboard (--practice-kb) and the home indicator.
 */
export function PracticeBottomBar({
  score, canPrevious, onPrevious, nextLabel, onNext, nextDisabled = false
}: {
  score: string
  canPrevious: boolean
  onPrevious: () => void
  nextLabel: string
  onNext: () => void
  nextDisabled?: boolean
}) {

  return (
    <div
      className="fixed inset-x-0 z-40 border-t border-gray-100 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md"
      style={{
        bottom: 'var(--practice-kb, 0px)'
      }}
    >
      <div className="mx-auto flex w-full max-w-[1200px] items-center gap-2.5 px-4 pt-2.5 pb-[calc(10px+env(safe-area-inset-bottom,0px))]">
        <span className="mr-auto text-[13px] text-gray-600 dark:text-gray-300 tabular-nums">{score}</span>
        <button type="button" disabled={!canPrevious} onClick={onPrevious} className={SOFT}>
          Previous
        </button>
        <button type="button" disabled={nextDisabled} onClick={onNext} className={MAIN}>
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

/** The wide practice area: breaks out of the portal's narrow column so two
 * panes fit, and is the container the layout queries. */
export const SOLVE_AREA_CLASS = 'practice-solve @container/solve min-w-0';

export const SOLVE_AREA_STYLE = {
  width: 'min(1200px, calc(100vw - 2rem))',
  marginLeft: 'calc(50% - min(600px, 50vw - 1rem))',
  paddingBottom: 'calc(96px + var(--practice-kb, 0px))'
} as const;
